import { useEffect, useState, type ReactNode } from 'react';
import type { AutomationKey, AutomationSettings, TradeTemplate } from '@buildbee/contracts';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Textarea,
  useToast,
} from '@buildbee/ui';
import { errorMessage, trpc, type RouterOutputs } from '../../../lib/trpc';

/**
 * Settings > Automations: switch on reminders for the team and follow-up emails for clients, and add
 * an industry (trade) template to an existing workspace. Everything is one toggle away; the extra
 * fields (timing, message) only show once an automation is on.
 */

type Data = RouterOutputs['workspace']['automations']['get'];
type MessageKey = 'proposal_follow_up' | 'proposal_expiring' | 'maintenance_check_in';

export function AutomationsSettingsPage() {
  const q = trpc.workspace.automations.get.useQuery();
  if (q.isLoading) return <LoadingState />;
  if (q.error || !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <div className="space-y-6">
      <AutomationsCard data={q.data} />
      <IndustryTemplateCard data={q.data} />
    </div>
  );
}

/** "3, 7, 14" <-> [3, 7, 14] (1-365, at most five, sorted, no repeats). */
function parseDays(text: string): number[] | null {
  const days = [
    ...new Set(
      text
        .split(/[\s,]+/)
        .filter(Boolean)
        .map(Number),
    ),
  ].sort((a, b) => a - b);
  if (!days.length || days.length > 5 || days.some((d) => !Number.isInteger(d) || d < 1 || d > 365))
    return null;
  return days;
}

function AutomationsCard({ data }: { data: Data }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const [draft, setDraft] = useState<AutomationSettings>(data.automations);
  const [daysText, setDaysText] = useState(data.automations.proposal_follow_up.days.join(', '));
  useEffect(() => {
    setDraft(data.automations);
    setDaysText(data.automations.proposal_follow_up.days.join(', '));
  }, [data.automations]);
  const save = trpc.workspace.automations.update.useMutation({
    onSuccess: async () => {
      toast.success('Automations saved');
      await Promise.all([utils.workspace.automations.invalidate(), utils.workspace.current.invalidate()]);
    },
  });
  const days = parseDays(daysText);
  const dirty =
    JSON.stringify(draft) !== JSON.stringify(data.automations) ||
    (days !== null && days.join(',') !== data.automations.proposal_follow_up.days.join(','));
  const set = <K extends AutomationKey>(key: K, patch: Partial<AutomationSettings[K]>) =>
    setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  const info = (key: AutomationKey) => data.catalog.find((c) => c.key === key)!;

  const onSave = () => {
    if (!days) return;
    save.mutate({ ...draft, proposal_follow_up: { ...draft.proposal_follow_up, days } });
  };

  const toggle = (key: AutomationKey, extra?: ReactNode) => {
    const c = info(key);
    const on = draft[key].enabled;
    return (
      <div key={key} className="rounded-lg bg-muted/60 p-3">
        <label className="flex cursor-pointer items-start gap-3">
          <Checkbox
            className="mt-0.5"
            checked={on}
            onCheckedChange={(v) =>
              set(key, { enabled: v === true } as Partial<AutomationSettings[typeof key]>)
            }
            aria-label={c.title}
          />
          <span>
            <span className="block text-sm font-medium">{c.title}</span>
            <span className="block text-xs text-muted-foreground">{c.description}</span>
          </span>
        </label>
        {on && extra ? <div className="mt-3 space-y-3 pl-[30px]">{extra}</div> : null}
      </div>
    );
  };

  const messageField = (key: MessageKey) => {
    const tags = info(key).tags ?? [];
    return (
      <Field
        label="Message"
        htmlFor={`auto-${key}-msg`}
        description={
          <>Leave blank to use the default shown. You can use {tags.map((t) => `{{${t}}}`).join(', ')}.</>
        }
      >
        <Textarea
          id={`auto-${key}-msg`}
          rows={3}
          value={draft[key].message ?? ''}
          placeholder={data.defaultMessages[key]}
          onChange={(e) =>
            set(key, { message: e.target.value || null } as Partial<AutomationSettings[typeof key]>)
          }
        />
      </Field>
    );
  };

  const numberField = (
    id: string,
    label: string,
    value: number,
    onChange: (n: number) => void,
    suffix: string,
  ) => (
    <Field label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          min={1}
          max={365}
          className="w-24"
          value={value}
          onChange={(e) => onChange(Math.max(1, Math.min(365, Number(e.target.value) || 1)))}
        />
        <span className="text-sm text-muted-foreground">{suffix}</span>
      </div>
    </Field>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Automations</CardTitle>
        <CardDescription>
          Reminders for your team and follow-ups for your clients. Turn on what you need; you can change it
          any time.
        </CardDescription>
      </CardHeader>
      <fieldset disabled={!data.canManage}>
        <CardContent className="space-y-6">
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">For your team</h3>
            <p className="text-xs text-muted-foreground">
              Shown in the bell menu (and on phones with the app).
            </p>
            {toggle('task_reminder')}
            {toggle(
              'stale_lead_reminder',
              numberField(
                'auto-stale-repeat',
                'Remind again every',
                draft.stale_lead_reminder.repeatDays,
                (n) => set('stale_lead_reminder', { repeatDays: n }),
                'days while the lead stays idle',
              ),
            )}
          </section>
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">For your clients (email)</h3>
            <p className="text-xs text-muted-foreground">
              Sent from your company name. Replies go to the person who owns the proposal or project.
            </p>
            {toggle(
              'proposal_follow_up',
              <>
                <Field
                  label="Send after"
                  htmlFor="auto-fu-days"
                  description="Days after the proposal was sent, separated by commas (up to five)."
                  error={days ? null : 'Enter 1 to 5 numbers between 1 and 365'}
                >
                  <Input
                    id="auto-fu-days"
                    className="w-48"
                    value={daysText}
                    onChange={(e) => setDaysText(e.target.value)}
                  />
                </Field>
                {messageField('proposal_follow_up')}
              </>,
            )}
            {toggle(
              'proposal_expiring',
              <>
                {numberField(
                  'auto-exp-days',
                  'Send',
                  draft.proposal_expiring.daysBefore,
                  (n) => set('proposal_expiring', { daysBefore: n }),
                  'days before it expires',
                )}
                {messageField('proposal_expiring')}
              </>,
            )}
            {toggle(
              'project_status_update',
              <ul className="space-y-1 text-xs text-muted-foreground">
                {data.statusMessages.map((m) => (
                  <li key={m.status}>
                    <span className="font-medium text-foreground">{STATUS_LABEL[m.status] ?? m.status}:</span>{' '}
                    {m.message}
                  </li>
                ))}
              </ul>,
            )}
            {toggle(
              'maintenance_check_in',
              <>
                {numberField(
                  'auto-maint-days',
                  'Send',
                  draft.maintenance_check_in.afterDays,
                  (n) => set('maintenance_check_in', { afterDays: n }),
                  'days after the project is completed (and every year after that)',
                )}
                {messageField('maintenance_check_in')}
              </>,
            )}
          </section>
          {save.error ? <p className="text-sm text-destructive">{errorMessage(save.error)}</p> : null}
        </CardContent>
        {data.canManage ? (
          <CardFooter className="justify-end gap-2">
            <Button
              variant="ghost"
              disabled={!dirty || save.isPending}
              onClick={() => {
                setDraft(data.automations);
                setDaysText(data.automations.proposal_follow_up.days.join(', '));
              }}
            >
              Discard
            </Button>
            <Button onClick={onSave} disabled={!dirty || !days} loading={save.isPending}>
              Save automations
            </Button>
          </CardFooter>
        ) : null}
      </fieldset>
    </Card>
  );
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Project starts',
  punch: 'Punch list',
  completed: 'Completed',
};

function IndustryTemplateCard({ data }: { data: Data }) {
  const utils = trpc.useUtils();
  const toast = useToast();
  const apply = trpc.workspace.automations.applyTradeTemplate.useMutation({
    onSuccess: async (r) => {
      const added = [...r.pipelinesAdded, ...r.projectTemplatesAdded];
      toast.success(
        added.length ? `Added: ${added.join(', ')}` : 'Template applied. You already had everything in it.',
      );
      await Promise.all([
        utils.workspace.automations.invalidate(),
        utils.workspace.current.invalidate(),
        utils.crm.pipelines.invalidate(),
      ]);
    },
  });
  const [pending, setPending] = useState<TradeTemplate | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Industry template</CardTitle>
        <CardDescription>
          Adds a trade’s sales pipeline and project templates to your workspace and tailors the follow-up
          messages above. Nothing you already have is changed.
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2">
        {data.trades.map((t) => {
          const current = data.tradeTemplate === t.id;
          return (
            <div key={t.id} className={`rounded-lg bg-muted/60 p-4 ${current ? 'ring-1 ring-primary' : ''}`}>
              <p className="text-sm font-medium">{t.name}</p>
              <p className="mb-2 text-xs text-muted-foreground">{t.blurb}</p>
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Pipelines:</span> {t.pipelines.join(', ')}
              </p>
              <p className="mb-3 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">Project templates:</span>{' '}
                {t.projectTemplates.join(', ')}
              </p>
              {data.canManage ? (
                <Button
                  size="sm"
                  variant={current ? 'outline' : 'primary'}
                  loading={apply.isPending && pending === t.id}
                  disabled={apply.isPending}
                  onClick={() => {
                    setPending(t.id);
                    apply.mutate({ trade: t.id });
                  }}
                >
                  {current ? 'Add anything missing' : 'Use this template'}
                </Button>
              ) : null}
              {current ? (
                <p className="mt-2 text-xs text-muted-foreground">Your workspace uses this template.</p>
              ) : null}
            </div>
          );
        })}
        {apply.error ? (
          <p className="text-sm text-destructive sm:col-span-2">{errorMessage(apply.error)}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
